import React, { useEffect, useMemo, useState } from "react";
import "./pages.css";          // you already have this
import "./blog.css";           // add the small CSS below
import AOS from "aos";
import "aos/dist/aos.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTag, faCalendarAlt, faArrowRight } from "@fortawesome/free-solid-svg-icons";

const POSTS = [
    {
    id: "cs-leadership-growth",
    title: "How Computer Science Made Me a Stronger Leader",
    date: "Dec 11, 2025",
    tags: ["Computer Science", "Leadership", "Technology Management"],
    cover: null,
    excerpt:
      "How debugging messy codebases, shipping team projects, and taking risks in Disruption Lab have shaped the kind of leader I want to be in tech and management.",
    body: (
      <>
        <p>
          When I first chose computer science, I thought I was only signing up
          to learn how to write efficient code. Over time, I realized that this
          major has also been a training ground for leadership. Every group
          project, late-night debugging session, and design critique has forced
          me to practice skills that look a lot more like management than just
          “coding”: prioritizing, communicating clearly under pressure, and
          taking responsibility when things break.
        </p>

        <p>
          Classwork has been a big part of that shift. In project-based CS
          courses, you don&apos;t succeed by being the smartest person quietly
          solving everything alone. You succeed by making sure the whole team
          understands the problem, agreeing on a plan, and keeping everyone
          aligned when requirements change or something unexpected fails. I&apos;ve
          learned to be the person who volunteers to draft the architecture, set
          milestones, and calmly say, “Okay, here&apos;s our next step,” when
          everyone else is stressed about a bug an hour before the deadline.
        </p>

        <p>
          Disruption Lab has amplified that learning. It&apos;s one thing to
          build a project for a grade, and another to build something that real
          stakeholders might actually use. Working on tech-and-management
          focused projects there pushed me to think beyond the code: Who is the
          user? What does “done” actually mean for them? How do we scope an
          ambitious idea into something we can ship this semester? In that
          environment, leadership isn&apos;t a title, it&apos;s noticing gaps,
          stepping in, and making sure the team keeps moving forward.
        </p>

        <p>
          A lot of my growth has come from taking risks. I&apos;ve said yes to
          projects where I didn&apos;t fully know what I was doing yet: learning
          new frameworks on the fly, presenting to people more experienced than
          me, or owning parts of a system that felt intimidating at first. Those
          moments forced me to get comfortable being uncomfortable. I learned how
          to ask for help without panicking, how to break unknowns into smaller,
          testable pieces, and how to admit, “I don&apos;t know yet, but I&apos;ll
          find out,” and then actually follow through.
        </p>

        <p>
          Leadership in a technical context also means being willing to make
          tradeoffs and own them. In both class projects and Disruption Lab, I&apos;ve
          had to decide when to refactor and when to ship, when to cut a feature
          to protect reliability, and how to communicate those decisions so
          teammates and stakeholders still feel heard. That balancing act between
          ideal engineering and realistic constraints is exactly the kind of
          judgment I&apos;ll need in a technology and management role.
        </p>

        <p>
          Another way CS has shaped my leadership is by teaching me how to
          create space for others. I know what it feels like to be the person
          who&apos;s scared to ask a “basic” question in a room full of confident
          engineers. Because of that, I try to be the teammate who explains
          concepts without being condescending, who documents decisions clearly,
          and who invites quieter voices into discussions. Whether I&apos;m
          pairing on a bug or reviewing someone&apos;s pull request, I see those
          moments as chances to help others grow, not just to get the task off
          my plate.
        </p>

        <p>
          All of this connects directly to my long-term goals in technology and
          management. I don&apos;t just want to be good at solving technical
          problems—I want to be good at organizing people, information, and
          systems so that we can solve bigger problems together. My computer
          science major has given me the tools to reason about complex systems,
          but it has also quietly trained me to lead: to communicate clearly,
          to take ownership, to navigate uncertainty, and to design processes
          that help teams do their best work.
        </p>

        <p>
          When a future employer looks at this blog post, I want them to see
          more than a list of classes or projects. I want them to see someone
          who has used computer science not just to become a better programmer,
          but to become a stronger, more thoughtful leader—someone ready to grow
          into roles at the intersection of engineering and management.
        </p>
      </>
    ),
  },

  {
    id: "tech-management-reflection",
    title: "Learning to Think Like a Tech Manager, Not Just an Engineer",
    date: "Nov 13, 2025",
    tags: ["Technology Management", "Career", "Course Reflection"],
    cover: null,
    excerpt:
      "This course shifted how I think about my career: from shipping features myself to designing systems, incentives, and teams that can ship them at scale.",
    body: (
      <>
        <p>
          When I declared computer science, I pictured my future almost entirely
          in code. I would be the engineer who could untangle impossible bugs,
          optimize the critical service, or prototype a new feature over a
          weekend. Management, in my mind, lived somewhere far in the distance,
          reserved for people who liked meetings more than pull requests. This
          course on technology and management quietly disrupted that picture.
          It made me realize that my long-term career goal is not just to build
          great systems, but to build the teams, processes, and culture that
          make those systems possible.
        </p>

        <p>
          One turning point was a guest speaker who leads product and engineering
          at a mid-stage software company. She described her role as
          “architecting decisions” rather than “making all the decisions.” At
          first that sounded like a buzzword, but she unpacked it in a way that
          stuck with me. Her job is to create enough clarity about strategy,
          metrics, and ownership that smart people across the organization can
          make good choices without waiting for her. As an engineer I usually
          focus on designing APIs and services; she made me see that managers
          design decision frameworks in exactly the same way.
        </p>

        <p>
          That idea clicked with a recurring theme in our lectures: technology
          decisions are almost never purely technical. When we looked at case
          studies of platform shifts, failed product launches, or delayed cloud
          migrations, the root cause was often organizational misalignment
          rather than a missing feature. Teams optimized for local success
          instead of system-wide outcomes. Incentives rewarded shipping quickly,
          not maintaining reliability. These examples taught me that if I want
          to have impact at scale, I will eventually need to care as much about
          incentives, communication, and structure as I do about algorithms and
          architecture.
        </p>

        <p>
          The course also pushed me to clarify my own aspirations. In one of the
          assignments, we had to map out a possible ten-year path that linked
          our technical interests with roles in technology management. Writing
          that plan forced me to be concrete: I see myself starting as a
          software engineer, then growing into a tech lead who owns a service
          end-to-end, and eventually moving into an engineering or product
          management role where I am responsible for a portfolio of systems and
          people. Instead of treating management as a vague “maybe someday,” I
          started treating it like a design problem I want to prepare for.
        </p>

        <p>
          A second key takeaway was how strongly good managers protect time for
          learning and experimentation. Several readings emphasized that
          innovation rarely comes from people who are running at 100% capacity
          on roadmap work. Our guest speaker shared that she explicitly budgets
          “optionality” into quarterly plans: leaving some engineering bandwidth
          unassigned so the team can investigate ideas, reduce hidden technical
          debt, or respond to unexpected opportunities. As a future manager, I
          want to adopt that mindset. It aligns with how I like to work as an
          engineer—rapidly prototyping, testing, and iterating—and scales it to
          an entire team.
        </p>

        <p>
          The course also changed how I think about communication. Before, I
          imagined “leadership communication” as writing formal updates or
          giving polished presentations. In practice, most influence happens in
          small conversations: framing a tradeoff clearly in a stand-up,
          asking the right question in a design review, or translating business
          goals into technical language that actually motivates engineers. Our
          discussions around stakeholder management drove this home. We talked
          about identifying who is affected by a decision, understanding what
          each group actually values, and tailoring messages to their concerns.
          Those skills are relevant whether I am convincing a team to invest in
          reliability or explaining a technical risk to a non-technical
          executive.
        </p>

        <p>
          Finally, this course made me more intentional about the kind of leader
          I hope to become. The frameworks we used to analyze case studies
          encouraged us to ask not just “Did this project succeed?” but “Would I
          be proud to have led it?” That includes how transparent the team was
          with users, how honestly they reported risks, and how they treated
          people when timelines slipped. In a future role, I want my leadership
          to be defined by three habits we kept coming back to in class:
          building psychological safety so engineers can surface bad news early;
          making decisions legible so people understand the “why” behind the
          roadmap; and measuring success not only by shipped features, but also
          by the growth of the people who built them.
        </p>

        <p>
          I am still excited about hard technical problems—debugging gnarly
          race conditions or designing clean data models will always be fun to
          me. But after this course, I no longer see “engineer” and “manager” as
          two separate tracks. Instead, I see them as different vantage points
          on the same challenge: how to use technology, people, and processes to
          create real value. As I move forward, I plan to seek out internships
          and roles where I can practice both sides—owning technical decisions
          while also learning how to coordinate teams, communicate strategy, and
          design systems of work. That combination, I believe, will make me not
          just a stronger engineer, but a more effective technology leader.
        </p>
      </>
    ),


  },
  // You can re-enable or add more posts like these later:
  // {
  //   id: "chi-ems",
  //   title: "What I Learned Building an EMS Piano Trainer",
  //   date: "Oct 5, 2025",
  //   tags: ["HCI", "Research", "EMS"],
  //   cover: null,
  //   excerpt:
  //     "Design notes from my CHI'25 work: why guidance intensity matters, and how we balanced comfort with learning speed.",
  //   body: (
  //     <>
  //       <p>
  //         This project explored whether gentle electrical stimulation could
  //         accelerate motor learning for piano novices…
  //       </p>
  //     </>
  //   ),
  // },
];

const ALL_TAGS = Array.from(new Set(POSTS.flatMap(p => p.tags))).sort();

const Blog = () => {
  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-in-out" });
  }, []);

  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("All");
  const [openPostId, setOpenPostId] = useState(POSTS[0]?.id ?? null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return POSTS.filter(p => {
      const tagMatch = activeTag === "All" || p.tags.includes(activeTag);
      const text =
        (p.title + " " + p.excerpt + " " + p.tags.join(" ")).toLowerCase();
      const qMatch = !q || text.includes(q);
      return tagMatch && qMatch;
    });
  }, [query, activeTag]);

  return (
    <section className="allInfo blog-page">
      {/* Header card */}
      <div className="card" data-aos="fade-right">
        <div className="details">
          <div className="content">
            <h2 className="mainTitle">blog.</h2>
            <h4>Short write-ups on projects, research, and design notes.</h4>
          </div>
        </div>
        <br />
        <br />
        <br />
        <br />
        <br />
      </div>

      <br />
      <br />

      {/* Controls card */}
      <div className="card" data-aos="fade-up">
        <div className="blog-controls">
          <input
            className="blog-search"
            placeholder="Search posts…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          <div className="blog-tags">
            <button
              className={`tag-pill ${activeTag === "All" ? "active" : ""}`}
              onClick={() => setActiveTag("All")}
            >
              <FontAwesomeIcon icon={faTag} /> All
            </button>
            {ALL_TAGS.map(t => (
              <button
                key={t}
                className={`tag-pill ${activeTag === t ? "active" : ""}`}
                onClick={() => setActiveTag(t)}
              >
                <FontAwesomeIcon icon={faTag} /> {t}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Posts list */}
      <div className="card" data-aos="fade-up">
        <h1 className="awardsTitle mainTitle">posts.</h1>

        <ul className="blog-list">
          {filtered.map(p => {
            const open = openPostId === p.id;
            return (
              <li key={p.id} className={`blog-item ${open ? "open" : ""}`}>
                <article>
                  <header className="blog-head">
                    <h3 className="blog-title">{p.title}</h3>
                    <div className="blog-meta">
                      <span className="blog-date">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {p.date}
                      </span>
                      <span className="blog-meta-sep">•</span>
                      <span className="blog-tags-line">
                        {p.tags.join("  ·  ")}
                      </span>
                    </div>
                  </header>

                  <p className="blog-excerpt">{p.excerpt}</p>

                  <div className="blog-actions">
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setOpenPostId(open ? null : p.id)}
                    >
                      {open ? "Hide" : "Read"}{" "}
                      <FontAwesomeIcon icon={faArrowRight} />
                    </button>
                  </div>

                  {open && (
                    <div className="blog-body" data-aos="fade-in">
                      {p.cover && (
                        <img
                          src={p.cover}
                          alt=""
                          className="blog-cover"
                          loading="lazy"
                        />
                      )}
                      <div className="blog-content">{p.body}</div>
                    </div>
                  )}
                </article>
              </li>
            );
          })}
          {filtered.length === 0 && (
            <li className="blog-empty">No posts match your filter.</li>
          )}
        </ul>
      </div>
    </section>
  );
};

export default Blog;